import {
  type IChapterContext,
  type IOnboardingChapter,
  ETourId,
} from '../types';
import { isTourCompleted } from '../persistence';
import { getTourConfig } from '../registry';
import { ONBOARDING_CHAPTERS } from '../onboarding-flow';
import { useTourStore, selectActiveTourId } from '../tours.store';

/**
 * Flat list of all tour IDs in the onboarding flow, including conditional
 * chapters (e.g. Exceptions). Used to determine `currentTourId`.
 */
const ONBOARDING_FLOW = ONBOARDING_CHAPTERS.flatMap(chapter => chapter.tours);

/**
 * Tour IDs required for flow completion. Excludes conditional chapters that
 * may never trigger for some users (e.g. Exceptions, which only runs if an
 * exception occurs during a draft run).
 */
const ONBOARDING_FLOW_REQUIRED = ONBOARDING_CHAPTERS.filter(
  chapter => !chapter.conditional,
).flatMap(chapter => chapter.tours);

/**
 * Get the index of the first uncompleted tour in the onboarding flow.
 * Returns -1 if the entire flow is completed.
 */
function getFlowPosition(): number {
  return ONBOARDING_FLOW.findIndex(
    tourId => !isTourCompleted(tourId, getTourConfig(tourId).persistence),
  );
}

/**
 * Get the total number of steps across all tours in a chapter.
 */
function getChapterTotalSteps(chapter: IOnboardingChapter): number {
  return chapter.tours.reduce(
    (total, tourId) => total + getTourConfig(tourId).steps.length,
    0,
  );
}

/**
 * Find which chapter a tour belongs to.
 */
function getChapterForTour(tourId: ETourId): IOnboardingChapter | undefined {
  return ONBOARDING_CHAPTERS.find(chapter => chapter.tours.includes(tourId));
}

/**
 * Hook to manage the unified onboarding flow.
 *
 * Returns the current position in the flow and chapter context so pages
 * can guard whether their tour should activate and display chapter-scoped
 * step counts on tour cards.
 *
 * @example
 * ```tsx
 * const { currentTourId, chapterContext } = useOnboardingFlow();
 *
 * // chapterContext.chapter.titleKey → "tours.singleFlow.chapter.essentials"
 * // chapterContext.totalSteps → 8 (all steps in chapter 1)
 * ```
 */
export function useOnboardingFlow() {
  // Subscribe to the active tour store so this hook re-evaluates when a
  // tour starts/ends. isTourCompleted() reads localStorage synchronously
  // and isn't reactive on its own, so without this subscription consumers
  // would see stale currentTourId after a tour completes.
  useTourStore(selectActiveTourId);

  const flowPosition = getFlowPosition();
  const currentTourId =
    flowPosition >= 0 ? ONBOARDING_FLOW[flowPosition] : null;

  let chapterContext: IChapterContext | null = null;

  /** Get the chapter context for the current tour. */
  if (currentTourId) {
    const chapter = getChapterForTour(currentTourId);
    if (chapter) {
      chapterContext = {
        chapter,
        totalSteps: getChapterTotalSteps(chapter),
      };
    }
  }

  return {
    /**
     * The first uncompleted tour across the entire flow (including
     * conditional chapters), or null only when every tour—including
     * conditional ones—is completed.
     *
     * Note: `isFlowComplete` may be true while `currentTourId` is still
     * set to a conditional tour (e.g. Exceptions) that hasn't been
     * triggered.
     */
    currentTourId,
    /**
     * Whether all required (non-conditional) tours have been completed.
     * Conditional chapters like Exceptions aren't required since they may
     * never trigger for some users.
     */
    isFlowComplete: ONBOARDING_FLOW_REQUIRED.every(tourId =>
      isTourCompleted(tourId, getTourConfig(tourId).persistence),
    ),
    /**
     * Chapter containing `currentTourId`, or null when every tour
     * (including conditional) is completed. Tracks `currentTourId`,
     * not `isFlowComplete`.
     */
    chapterContext,
  };
}
